<script lang="ts" setup>
import type { ResponseResult } from '~kui/types/formResult';

import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

interface Props {
  email: string;
  loading: boolean;
  initialCode?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  submit: [code: string];
  back: [];
  resend: [onComplete: () => void];
}>();

const { t } = useI18n();

const isResent = ref(false);
const resendLoading = ref(false);
const formKey = ref(0);
const formResult = ref<ResponseResult | null>(null);

const formConfig = defineFormConfig(
  {
    otpCode: defineField({
      type: 'pincode',
      options: {
        required: true,
        label: t('general.formFields.otpCode.label'),
        hideLabel: true,
        focusOnMounted: true,
        nextFocusOnSubmitButton: true,
      },
      initialValue: props.initialCode ?? '',
      hint: t('general.formFields.otpCode.hint'),
      schema: z.string().length(6, t('general.formFields.otpCode.tooShort')).regex(/^\d+$/, t('general.formFields.otpCode.onlyNumbers')),
    }),
  },
  t('general.auth.submitButton.submit'),
);

const handleSubmit = (value: z.infer<typeof formConfig.schema>) => {
  formResult.value = null;
  emit('submit', value.otpCode);
};

const handleResend = () => {
  isResent.value = false;
  resendLoading.value = true;
  emit('resend', () => {
    isResent.value = true;
    resendLoading.value = false;
  });
};

const clearAndReset = () => {
  formKey.value++;
  formResult.value = null;
};

const setError = (message: string) => {
  formResult.value = { success: false, message };
  resendLoading.value = false;
};

defineExpose({ clearAndReset, setError });
</script>

<template>
  <div
    class="flex flex-col items-center"
    data-testid="auth-otp-step"
  >
    <Heading
      size="xl"
      as="h1"
      class="text-center mb-3"
    >
      {{ t('general.auth.verifyEmail') }}
    </Heading>
    <Text size="lg" class="text-center">
      {{ t('general.auth.enterCodeSentTo') }}
    </Text>
    <Text size="lg" class="text-center font-medium">
      {{ email }}
    </Text>
    <Button
      class="mb-2"
      variant="link"
      size="sm"
      type="button"
      :disabled="loading"
      data-testid="auth-otp-back"
      @click="emit('back')"
    >
      {{ t('general.auth.changeEmailButton') }}
    </Button>
    <KUICoreForm
      :key="formKey"
      class="w-full text-center"
      :form-config="formConfig"
      :form-result="formResult"
      :loading="loading"
      :allow-clean-submit="!!props.initialCode"
      submit-button-size="lg"
      @submit="handleSubmit"
    />

    <div class="mt-8 flex flex-col items-center">
      <Text size="sm">
        {{ t('general.formFields.cantLogin.labelAlt') }}
      </Text>
      <Text
        v-if="isResent"
        as="span"
        size="sm"
        class="text-success-foreground font-medium"
      >
        {{ t('general.auth.resentOtpCode', { email }) }}
      </Text>
      <Button
        class="mb-2"
        variant="link"
        size="sm"
        :loading="resendLoading"
        type="button"
        @click="handleResend"
      >
        {{ t('general.formFields.resendCode.label') }}
      </Button>
    </div>
  </div>
</template>
